import {
  ChartData,
  ChartMetaInfo,
  ChartTemplate,
  ChartTemplateContent,
  IExternalSaveLoadAdapter,
  LineToolsAndGroupsLoadRequestContext,
  LineToolsAndGroupsLoadRequestType,
  LineToolsAndGroupsState,
  LineToolState,
  ResolutionString,
  StudyTemplateData,
  StudyTemplateMetaInfo,
} from '../charting_library/charting_library';
import { localStorageKeys } from './localstorageKeys';

// Heavily inspired by https://codepen.io/tradingview/pen/ExJjxjP

// Storage keys for different data types
const storageKeys = {
  charts: localStorageKeys.tvCharts,
  studyTemplates: localStorageKeys.tvStudyTemplates,
  drawingTemplates: localStorageKeys.tvDrawingTemplates,
  chartTemplates: localStorageKeys.tvChartTemplates,
  drawings: localStorageKeys.tvDrawings,
} as const;

const AUTO_SAVE_INTERVAL = 1000;

interface SavedChartData {
  timestamp: number;
  id: string;
  content: string;
  name?: string;
  symbol?: string;
  resolution?: ResolutionString;
}

interface DrawingTemplate {
  name: string;
  toolName: string;
  content: string;
}

interface SavedChartTemplate {
  name: string;
  content: ChartTemplateContent;
}

type LayoutDrawings = Record<string, LineToolState>;
type SavedDrawings = Record<string, LayoutDrawings>;

// LocalStorage adapter for saving charts with drawings
export class LocalStorageChartAdapter implements IExternalSaveLoadAdapter {
  private _charts: SavedChartData[] = [];
  private _studyTemplates: StudyTemplateData[] = [];
  private _drawingTemplates: DrawingTemplate[] = [];
  private _chartTemplates: SavedChartTemplate[] = [];
  private _isDirty = false;
  protected _drawings: SavedDrawings = {};
  private readonly DEBUG = false;

  constructor() {
    // Load all data from localStorage on initialization
    this._charts = this._getFromLocalStorage<SavedChartData[]>(storageKeys.charts) ?? [];
    this._studyTemplates =
      this._getFromLocalStorage<StudyTemplateData[]>(storageKeys.studyTemplates) ?? [];
    this._drawingTemplates =
      this._getFromLocalStorage<DrawingTemplate[]>(storageKeys.drawingTemplates) ?? [];
    this._chartTemplates =
      this._getFromLocalStorage<SavedChartTemplate[]>(storageKeys.chartTemplates) ?? [];
    this._drawings = this._getFromLocalStorage<SavedDrawings>(storageKeys.drawings) ?? {};

    // Set up periodic save to localStorage
    setInterval(() => {
      if (this._isDirty) {
        this._saveAllToLocalStorage();
        this._isDirty = false;
      }
    }, AUTO_SAVE_INTERVAL);
  }

  private log(message: string, ...args: unknown[]) {
    if (this.DEBUG) {
      console.log(`[TV Chart Adapter] ${message}`, ...args);
    }
  }

  // Chart methods
  async getAllCharts(): Promise<ChartMetaInfo[]> {
    return Promise.resolve(
      this._charts.map((chart) => ({
        id: chart.id,
        name: chart.name || `Chart ${chart.id}`,
        symbol: chart.symbol || '',
        resolution: chart.resolution || ('1D' as ResolutionString),
        timestamp: chart.timestamp,
      }))
    );
  }

  async removeChart(id: string | number) {
    const index = this._charts.findIndex((c) => c.id === String(id));
    if (index !== -1) {
      this._charts.splice(index, 1);
      this._isDirty = true;
      return Promise.resolve();
    }
    return Promise.reject(new Error('The chart does not exist'));
  }

  async saveChart(chartData: ChartData): Promise<string> {
    const id = chartData.id || this._generateUniqueChartId();

    if (chartData.id) {
      await this.removeChart(chartData.id);
    }

    const savedChartData: SavedChartData = {
      id: String(id),
      content: chartData.content,
      timestamp: Math.round(Date.now() / 1000),
      name: chartData.name,
      symbol: chartData.symbol,
      resolution: chartData.resolution,
    };

    this._charts.push(savedChartData);
    this._isDirty = true;

    this.log('Saved chart:', savedChartData.id);

    return Promise.resolve(savedChartData.id);
  }

  async getChartContent(id: string | number): Promise<string> {
    const chart = this._charts.find((c) => c.id === String(id));
    if (chart) {
      this.log('Found chart content for id:', id);
      return Promise.resolve(chart.content);
    }
    return Promise.reject(new Error('The chart does not exist'));
  }

  // Study template methods
  async getAllStudyTemplates(): Promise<StudyTemplateData[]> {
    return Promise.resolve(this._studyTemplates);
  }

  async removeStudyTemplate(studyTemplateData: StudyTemplateMetaInfo): Promise<void> {
    for (let i = 0; i < this._studyTemplates.length; ++i) {
      if (this._studyTemplates[i]?.name === studyTemplateData.name) {
        this._studyTemplates.splice(i, 1);
        this._isDirty = true;
        return Promise.resolve();
      }
    }
    return Promise.reject(new Error('The study template does not exist'));
  }

  async saveStudyTemplate(studyTemplateData: StudyTemplateData) {
    for (let i = 0; i < this._studyTemplates.length; ++i) {
      if (this._studyTemplates[i]?.name === studyTemplateData.name) {
        this._studyTemplates.splice(i, 1);
        break;
      }
    }
    this._studyTemplates.push(studyTemplateData);
    this._isDirty = true;
    return Promise.resolve();
  }

  async getStudyTemplateContent(studyTemplateData: StudyTemplateMetaInfo): Promise<string> {
    for (let i = 0; i < this._studyTemplates.length; ++i) {
      if (this._studyTemplates[i]?.name === studyTemplateData.name) {
        return Promise.resolve(this._studyTemplates[i]!.content);
      }
    }
    return Promise.reject(new Error('The study template does not exist'));
  }

  // Drawing template methods
  async getDrawingTemplates(): Promise<string[]> {
    return Promise.resolve(
      this._drawingTemplates.map((template: DrawingTemplate) => template.name)
    );
  }

  async removeDrawingTemplate(toolName: string, templateName: string): Promise<void> {
    for (let i = 0; i < this._drawingTemplates.length; ++i) {
      if (
        this._drawingTemplates[i]?.name === templateName &&
        this._drawingTemplates[i]?.toolName === toolName
      ) {
        this._drawingTemplates.splice(i, 1);
        this._isDirty = true;
        return Promise.resolve();
      }
    }
    return Promise.reject(new Error('The drawing template does not exist'));
  }

  async saveDrawingTemplate(
    toolName: string,
    templateName: string,
    content: string
  ): Promise<void> {
    for (let i = 0; i < this._drawingTemplates.length; ++i) {
      if (
        this._drawingTemplates[i]?.name === templateName &&
        this._drawingTemplates[i]?.toolName === toolName
      ) {
        this._drawingTemplates.splice(i, 1);
        break;
      }
    }
    this._drawingTemplates.push({
      name: templateName,
      content: content,
      toolName: toolName,
    });
    this._isDirty = true;
    return Promise.resolve();
  }

  async loadDrawingTemplate(toolName: string, templateName: string): Promise<string> {
    for (let i = 0; i < this._drawingTemplates.length; ++i) {
      if (
        this._drawingTemplates[i]?.name === templateName &&
        this._drawingTemplates[i]?.toolName === toolName
      ) {
        return Promise.resolve(this._drawingTemplates[i]!.content);
      }
    }
    return Promise.reject(new Error('The drawing template does not exist'));
  }

  // Chart template methods
  async getAllChartTemplates(): Promise<string[]> {
    return this._chartTemplates.map((x) => x.name);
  }

  async saveChartTemplate(templateName: string, content: ChartTemplateContent): Promise<void> {
    const theme = this._chartTemplates.find((x) => x.name === templateName);
    if (theme) {
      theme.content = content;
    } else {
      this._chartTemplates.push({ name: templateName, content });
    }
    this._isDirty = true;
  }

  async removeChartTemplate(templateName: string): Promise<void> {
    this._chartTemplates = this._chartTemplates.filter((x) => x.name !== templateName);
    this._isDirty = true;
  }

  async getChartTemplateContent(templateName: string): Promise<ChartTemplate> {
    const template = this._chartTemplates.find((x) => x.name === templateName);
    if (!template || !template.content) {
      return Promise.reject(new Error('The chart template does not exist'));
    }
    return {
      content: JSON.parse(JSON.stringify(template.content)), // Deep clone
    };
  }

  // Drawing save/load methods - only used if saveload_separate_drawings_storage is enabled
  // Currently disabled.
  async saveLineToolsAndGroups(
    layoutId: string,
    chartId: string | number,
    state: LineToolsAndGroupsState
  ): Promise<void> {
    const drawings = state.sources;
    if (!drawings) return;

    const drawingKey = this._getDrawingKey(layoutId, chartId);
    if (!this._drawings[drawingKey]) {
      this._drawings[drawingKey] = {};
    }

    for (let [key, state] of drawings) {
      if (state === null) {
        delete this._drawings[drawingKey][key];
      } else {
        this._drawings[drawingKey][key] = state;
      }
    }
    this._isDirty = true;
    this.log('Saved drawings for', layoutId, chartId, this._drawings);
  }

  async loadLineToolsAndGroups(
    layoutId: string | undefined,
    chartId: string | number,
    _requestType: LineToolsAndGroupsLoadRequestType,
    _requestContext: LineToolsAndGroupsLoadRequestContext
  ): Promise<Partial<LineToolsAndGroupsState> | null> {
    if (!layoutId) {
      return null;
    }
    const rawSources = this._drawings[this._getDrawingKey(layoutId, chartId)];
    if (!rawSources) return null;

    const sources = new Map();
    for (let [key, state] of Object.entries(rawSources)) {
      sources.set(key, state);
    }

    this.log('Loaded drawings for', layoutId, chartId, sources);
    return {
      sources,
    };
  }

  // Helper methods
  private _generateUniqueChartId(): string {
    return crypto.randomUUID();
  }

  protected _getFromLocalStorage<T>(key: string): T {
    const dataFromStorage = window.localStorage.getItem(key);
    return JSON.parse(dataFromStorage || 'null');
  }

  protected _saveToLocalStorage(key: string, data: unknown): void {
    const dataString = JSON.stringify(data);
    window.localStorage.setItem(key, dataString);
  }

  protected _saveAllToLocalStorage(): void {
    this._saveToLocalStorage(storageKeys.charts, this._charts);
    this._saveToLocalStorage(storageKeys.studyTemplates, this._studyTemplates);
    this._saveToLocalStorage(storageKeys.drawingTemplates, this._drawingTemplates);
    this._saveToLocalStorage(storageKeys.chartTemplates, this._chartTemplates);
    this._saveToLocalStorage(storageKeys.drawings, this._drawings);
  }

  private _getDrawingKey(layoutId: string, chartId: string | number): string {
    return `${layoutId}/${chartId}`;
  }
}
